import { useState, useEffect, useRef } from "react";
import PortalConfetti from "./PortalConfetti";
import { doc, setDoc, getDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { CHARACTERS_MAP } from "../maps";
import { CHARACTERS } from "../data";
import { getDailyItem, updateGameStats } from "../utils/gameLogic";
import { usePersistentStorage, useKeyboardNavigation } from "../hooks/useGameHooks";
import { motion, AnimatePresence } from "framer-motion";

/** Canonical game release order for the debut-game comparison arrows (↑ earlier / ↓ later). */
export const GAME_ORDER = [
  "FC", "SC", "3rd", "Zero", "Azure",
  "CS1", "CS2", "CS3", "CS4", "Reverie",
  "Daybreak", "Daybreak 2",
];

/**
 * Character guessing mode — 10 guesses with a 6-trait comparison grid (Gender, Weapon,
 * Nation, Affiliation, Age, Debut). Supports search autocomplete with keyboard nav,
 * theme-specific grid-cell animations, and Firestore stat syncing.
 *
 * @param {{ version: string, targetDateObj: Date, targetDateStr: string, isArchive: boolean, setIsModalOpen: (v: boolean) => void, guessedCharacters: Array<Object>, setGuessedCharacters: (g: Array<Object>) => void, currentTheme: string }} props
 * @param {string} props.version - "old" or "new".
 * @param {Date} props.targetDateObj - Target date object.
 * @param {string} props.targetDateStr - Date string for localStorage keys.
 * @param {boolean} props.isArchive - Whether viewing an archived date.
 * @param {(v: boolean) => void} props.setIsModalOpen - Opens global stats modal.
 * @param {Array<Object>} props.guessedCharacters - Previously submitted character guesses.
 * @param {(g: Array<Object>) => void} props.setGuessedCharacters - Guess state setter.
 * @param {string} props.currentTheme - Active era theme for cell animations.
 * @returns {JSX.Element} The character game UI.
 */
export default function CharacterMode({
  version,
  targetDateObj,
  targetDateStr,
  isArchive,
  setIsModalOpen,
  guessedCharacters,
  setGuessedCharacters,
  currentTheme,
}) {
  const [currentGuess, setCurrentGuess] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const [collection, setCollection] = usePersistentStorage(
    "kisekidle-collection",
    []
  );
  const [isCopied, setIsCopied] = useState(false);
  const targetCharacter = getDailyItem(
    CHARACTERS,
    targetDateObj,
    CHARACTERS_MAP
  );

  const MAX_GUESSES = 10;
  const hasWon = guessedCharacters.some(
    (char) => char.id === targetCharacter.id
  );
  const guessesLeft = MAX_GUESSES - guessedCharacters.length;
  const isGameOver = guessesLeft <= 0 && !hasWon;

  const [globalStats, setGlobalStats] = useState(null);

  // --- ANTI-CHEAT WIN FLAG ---
  useEffect(() => {
    if (hasWon) {
      const storageKey = `kisekidle-chars-${targetDateStr}`;
      const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      storedData.hasWon = true;
      localStorage.setItem(storageKey, JSON.stringify(storedData));
    }
  }, [hasWon, targetDateStr]);

  /** Filters characters by search query, excluding already-guessed ones, and sorts results. */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentGuess(value);
    setFocusedIndex(-1);
    if (value.length > 0) {
      const query = value.toLowerCase().trim();
      const filteredChars = CHARACTERS.filter((char) =>
        char.name.toLowerCase().includes(query) &&
        !guessedCharacters.some((g) => g.id === char.id)
      ).sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        const aStartsWith = nameA.startsWith(query);
        const bStartsWith = nameB.startsWith(query);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return nameA.localeCompare(nameB);
      });
      setSuggestions(filteredChars.slice(0, 15));
    } else {
      setSuggestions([]);
    }
  };

  const [showEndScreen, setShowEndScreen] = useState(hasWon || isGameOver);

  useEffect(() => {
    if (hasWon || isGameOver) {
      const timer = setTimeout(() => {
        setShowEndScreen(true);
      }, 3800);
      return () => clearTimeout(timer);
    }
  }, [hasWon, isGameOver]);

  const syncInProgress = useRef(false);

  // --- CHARACTER MODE TRACKING ---
  useEffect(() => {
    const syncReceipt = `synced-character-${targetDateStr}`;
    const hasAlreadySynced = localStorage.getItem(syncReceipt);

    if (showEndScreen && !hasAlreadySynced && !syncInProgress.current) {
      const updateAndFetchStats = async () => {
        syncInProgress.current = true;
        try {
          const statsRef = doc(db, "character_stats", targetDateStr);
          const updateData = {
            totalPlays: increment(1),
            totalWins: hasWon ? increment(1) : increment(0),
            totalWinningAttempts: hasWon
              ? increment(guessedCharacters.length)
              : increment(0),
            guessTally: {},
          };

          guessedCharacters.forEach((char) => {
            if (char && char.name) {
              updateData.guessTally[char.name] = increment(1);
            }
          });

          await setDoc(statsRef, updateData, { merge: true });
          localStorage.setItem(syncReceipt, "true");

          const updatedDoc = await getDoc(statsRef);
          if (updatedDoc.exists()) {
            setGlobalStats(updatedDoc.data());
          }
        } catch (error) {
          console.error("Error saving stats to Firebase:", error);
        } finally {
          syncInProgress.current = false;
        }
      };

      updateAndFetchStats();
    }
  }, [showEndScreen, targetDateStr, hasWon, guessedCharacters.length]);

  /** Processes a character guess — checks win condition, updates stats, adds to collection. */
  const handleGuess = (selectedChar) => {
    if (guessedCharacters.find((c) => c.id === selectedChar.id)) return;

    const newGuesses = [...guessedCharacters, selectedChar];
    const isWin = selectedChar.id === targetCharacter.id;
    const isLoss = newGuesses.length >= MAX_GUESSES && !isWin;

    const selectedId = String(selectedChar.id);
    if (isWin && !collection.some((id) => String(id) === selectedId)) {
      setCollection([...collection, selectedId]);
    }

    if (!isArchive && (isWin || isLoss)) {
      updateGameStats("character", isWin, newGuesses.length, MAX_GUESSES);
    }

    setGuessedCharacters(newGuesses);
    setCurrentGuess("");
    setSuggestions([]);
  };

  const { focusedIndex, setFocusedIndex, handleKeyDown, listRef } =
    useKeyboardNavigation(suggestions, handleGuess);

  /**
   * Theme-specific entrance animation variants for the trait comparison grid cells.
   * Each era gets a distinct visual style (spring rotate, slide with brightness, etc.).
   * @type {Object<string, {initial: Object, animate: (delay: number) => Object}>}
   */
  const cellVariants = {
    sky: {
      initial: { rotateZ: 45, opacity: 0, scale: 0.8 },
      animate: (delay) => ({
        rotateZ: 0,
        opacity: 1,
        scale: 1,
        transition: {
          type: "spring",
          stiffness: 40,
          damping: 15,
          delay: delay,
        },
      }),
    },
    coldsteel: {
      initial: { x: 50, opacity: 0, filter: "brightness(2)" },
      animate: (delay) => ({
        x: 0,
        opacity: 1,
        filter: "brightness(1)",
        transition: {
          type: "spring",
          stiffness: 80,
          damping: 18,
          delay: delay,
        },
      }),
    },
    daybreak: {
      initial: { rotateY: 90, opacity: 0, scale: 0.9 },
      animate: (delay) => ({
        rotateY: 0,
        opacity: 1,
        scale: 1,
        transition: {
          duration: 1.2,
          delay: delay,
          ease: [0.4, 0, 0.2, 1],
        },
      }),
    },
    legacy: {
      initial: { opacity: 0, scale: 0.9, filter: "brightness(2) blur(10px)" },
      animate: (delay) => ({
        opacity: 1,
        scale: 1,
        filter: "brightness(1) blur(0px)",
        transition: {
          duration: 0.6,
          delay: delay,
          ease: [0.4, 0, 0.2, 1],
        },
      }),
    },
  };

  /** Returns framer-motion initial/animate props for a grid cell based on the current theme. */
  const getAnimationProps = (delay) => {
    const theme = currentTheme || "legacy";
    const variant = cellVariants[theme] || cellVariants.legacy;
    return {
      initial: variant.initial,
      animate: variant.animate(delay),
    };
  };

  /**
   * Compares a guessed trait against the target trait.
   * Returns "correct" for exact match, "partial" for overlapping arrays, "incorrect" otherwise.
   *
   * @param {*} guessVal - The guessed character's trait value (string or array).
   * @param {*} targetVal - The target character's trait value.
   * @returns {"correct" | "partial" | "incorrect"}
   */
  const getMatchStatus = (guessVal, targetVal) => {
    // 1. Normalize both inputs into arrays of clean, lowercase strings
    const normalize = (val) => {
      if (val === null || val === undefined) return [];
      const arr = Array.isArray(val) ? val : [val];
      return arr.map((item) => String(item).toLowerCase().trim());
    };

    const guessArr = normalize(guessVal);
    const targetArr = normalize(targetVal);

    // 2. Check for an exact match
    const isExactMatch =
      guessArr.length === targetArr.length &&
      guessArr.every((item) => targetArr.includes(item)) &&
      targetArr.every((item) => guessArr.includes(item));

    if (isExactMatch) return "correct";

    // 3. Check for a partial match
    if (guessArr.some((item) => targetArr.includes(item))) return "partial";

    // 4. No match
    return "incorrect";
  };

  /** Joins array traits into a display string, or returns the raw value for scalars. */
  const formatTrait = (trait) =>
    Array.isArray(trait) ? trait.join(", ") : trait;

  /** Converts an age string to a number, falling back to predictedAgeRange midpoint for "Unknown". */
  const getNumericAge = (age, charObj) => {
    if (age !== "Unknown") return parseInt(age, 10);
    if (charObj && charObj.predictedAgeRange) {
      return (charObj.predictedAgeRange.min + charObj.predictedAgeRange.max) / 2;
    }
    return null;
  };

  /** Returns an ↑ or ↓ arrow comparing guessed age to target age (null if equal or unguessable). */
  const getAgeArrow = (guessAge, guessChar, targetAge, targetChar) => {
    const gAge = getNumericAge(guessAge, guessChar);
    const tAge = getNumericAge(targetAge, targetChar);

    if (gAge === null || tAge === null || gAge === tAge) return null;
    const arrow = gAge < tAge ? "↑" : "↓";
    return <span className="hint-arrow">{arrow}</span>;
  };

  /** Returns ↑ or ↓ arrow based on GAME_ORDER position (null if same game or unknown). */
  const getGameArrow = (guessGame, targetGame) => {
    const gIndex = GAME_ORDER.indexOf(guessGame);
    const tIndex = GAME_ORDER.indexOf(targetGame);
    if (gIndex === -1 || tIndex === -1 || gIndex === tIndex) return null;
    const arrow = gIndex < tIndex ? "↑" : "↓";
    return <span className="hint-arrow">{arrow}</span>;
  };

  /** Copies shareable character-mode results (emoji grid or text list) to clipboard. */
  const handleShare = (format) => {
    const score = hasWon ? guessedCharacters.length : "X";
    let shareText = `Kisekidle - Character (${targetDateStr})\nGuesses: ${score}/${MAX_GUESSES}\n\n`;

    if (format === "emoji") {
      guessedCharacters.forEach((char) => {
        const traits = [
          getMatchStatus(char.gender, targetCharacter.gender),
          getMatchStatus(char.weapon, targetCharacter.weapon),
          getMatchStatus(char.nationality, targetCharacter.nationality),
          getMatchStatus(char.affiliation, targetCharacter.affiliation),
          getMatchStatus(char.age, targetCharacter.age),
          getMatchStatus(char.debutGame, targetCharacter.debutGame),
        ];

        const rowEmojis = traits
          .map((status) => {
            if (status === "correct") return "🟩";
            if (status === "partial") return "🟧";
            return "🟥";
          })
          .join("");

        shareText += rowEmojis + "\n";
      });
    } else {
      guessedCharacters.forEach((char, i) => {
        shareText += `${i + 1}. ${char.name} ${char.name === targetCharacter.name ? "✅" : "❌"}\n`;
      });
    }

    shareText += `\nhttps://kisekidle.app/`;

    navigator.clipboard.writeText(shareText).then(() => {
      setIsCopied(format);
      setTimeout(() => setIsCopied(null), 2000);
    });
  };

  return (
    <div className="game-terminal">
      <div className="header-container">
        <h2 className="mode-title">Guess the Character</h2>
        <div className="guesses-remaining">
          Guesses Remaining: {guessesLeft}
        </div>
      </div>

      {hasWon && showEndScreen && (
        <div className="victory-box">
          <PortalConfetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={400}
            gravity={0.15}
          />
          <h3>🎉 You got it! 🎉</h3>
          <p>
            The character was <strong>{targetCharacter.name}</strong>.
          </p>

          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger premium-hover"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      {isGameOver && showEndScreen && (
        <div className="game-over-box">
          <h3>❌ Out of guesses! ❌</h3>
          <p>
            The correct character was <strong>{targetCharacter.name}</strong>.
          </p>

          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger premium-hover"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}
      {!showEndScreen && (
        <div className="search-container">
           <input
            type="text"
            className="search-input"
            value={currentGuess}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Search for a character..."
            disabled={hasWon || isGameOver}
            autoFocus
          />
          <button
            className="submit-button premium-hover"
            disabled={hasWon || isGameOver}
            onClick={() =>
              suggestions.length > 0 && handleGuess(suggestions[0])
            }
          >
            Submit Guess
          </button>
          {suggestions.length > 0 && (
            <ul className="suggestions-list" ref={listRef}>
              {suggestions.map((c, index) => (
                <li
                  key={c.id}
                  className={`${index === focusedIndex ? "focused" : ""} suggestion-item`}
                  onClick={() => handleGuess(c)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  {c.imageUrl && (
                    <img
                      src={c.imageUrl}
                      alt={c.name}
                      className="suggestion-image"
                    />
                  )}
                  <span>{c.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid-container">
        {guessedCharacters.length > 0 && (
          <div className="grid-header">
            <div>Character</div>
            <div>Gender</div>
            <div>Weapon</div>
            <div>Nation</div>
            <div>Affiliation</div>
            <div>Age</div>
            <div>Debut</div>
          </div>
        )}
        <AnimatePresence initial={false}>
          {guessedCharacters
            .slice()
            .reverse()
            .map((char, rowIndex) => (
              <motion.div
                key={char.id}
                className="grid-row"
                layout
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                  mass: 1
                }}
              >
                {/* Name - Flips immediately */}
                <motion.div
                  className="grid-cell"
                  {...getAnimationProps(0)}
                >
                  {char.imageUrl ? (
                    <img
                      src={char.imageUrl}
                      alt={char.name}
                      style={{
                        width: "88px",
                        height: "88px",
                        borderRadius: "1px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    char.name
                  )}
                </motion.div>

                {/* Gender */}
                <motion.div
                  className={`grid-cell ${getMatchStatus(char.gender, targetCharacter.gender)}`}
                  {...getAnimationProps(0.3)}
                >
                  {formatTrait(char.gender)}
                </motion.div>

                {/* Weapon */}
                <motion.div
                  className={`grid-cell ${getMatchStatus(char.weapon, targetCharacter.weapon)}`}
                  {...getAnimationProps(0.6)}
                >
                  {formatTrait(char.weapon)}
                </motion.div>

                {/* Nation */}
                <motion.div
                  className={`grid-cell ${getMatchStatus(char.nationality, targetCharacter.nationality)}`}
                  {...getAnimationProps(0.9)}
                >
                  {formatTrait(char.nationality)}
                </motion.div>

                {/* Affiliation */}
                <motion.div
                  className={`grid-cell ${getMatchStatus(char.affiliation, targetCharacter.affiliation)}`}
                  {...getAnimationProps(1.2)}
                >
                  {formatTrait(char.affiliation)}
                </motion.div>

                {/* Age */}
                <motion.div
                  className={`grid-cell ${getMatchStatus(char.age, targetCharacter.age)}`}
                  {...getAnimationProps(1.5)}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      {char.age}
                      {getAgeArrow(
                        char.age,
                        char,
                        targetCharacter.age,
                        targetCharacter
                      )}
                    </div>
                    {char.age === "Unknown" && char.predictedAgeRange && (
                      <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>
                        ({char.predictedAgeRange.min}-{char.predictedAgeRange.max})
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Debut */}
                <motion.div
                  className={`grid-cell ${getMatchStatus(char.debutGame, targetCharacter.debutGame)}`}
                  {...getAnimationProps(1.8)}
                >
                  {char.debutGame}{" "}
                  {getGameArrow(char.debutGame, targetCharacter.debutGame)}
                </motion.div>
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
